import axios, { AxiosError } from 'axios'
import {
  ListResponse,
  SimpleResponse,
  SingleResponse,
} from '../interfaces/Shipment/ListResponseDto'
import {
  DepartmentInterfase,
  Nullable,
  ProfileInterfase,
  UpdStarStandUserInterfase,
  UserAccountsCountInterfase,
  UserAccountsExitsInterfase,
  UserAccountsInterfase,
} from '../interfaces'
import { UserAccountMapperDto } from '../utils/mappers'
import { mapUserApiError } from '../utils/userValidation'

const { REACT_APP_URL_PATH_API } = process.env

export const getAllUser = async (
  accountID: string,
  accountSiteID?: string,
  userAccountID?: string
): Promise<ListResponse<UserAccountsInterfase[]> | undefined> => {
  // `AccountSiteID` es `int?`: si va vacío o con "NaN" el binder responde 400 y
  // la pantalla quedaba girando en "Cargando...". Cuando no hay valor, el
  // parámetro sencillamente no se manda.
  const siteID = parseInt(accountSiteID ?? '')
  const siteQuery = Number.isNaN(siteID) ? '' : `&AccountSiteID=${siteID}`

  var response = await axios
    .get(
      `${REACT_APP_URL_PATH_API}/users/GetUserAccount?AccountID=${accountID}&ApplicationID=2${siteQuery}&UserAccountID=${
        userAccountID ?? ''
      }`
    )
    .then(function (response) {
      var responsedto = response.data as ListResponse<UserAccountsInterfase[]>
      return responsedto
    })
    .catch(function (error) {
      // OJO: antes se devolvía el AxiosResponse crudo, que no trae `didError`
      // ni `model`. Quien llamaba lo daba por bueno y reventaba al leer
      // `model[0]`, dejando el modal de carga abierto para siempre.
      const axiosError = error as AxiosError
      const mapped = mapUserApiError(
        axiosError.response?.data,
        axiosError.message ?? 'No se pudieron cargar los usuarios'
      )
      console.log('Error cargando los usuarios:', mapped.message)
      return {
        model: [],
        didError: true,
        message: mapped.message,
        errorMessage: mapped.message,
      } as ListResponse<UserAccountsInterfase[]>
    })

  return response
}

export const getAllDepartament = async (): Promise<
  ListResponse<DepartmentInterfase[]> | undefined
> => {
  var response = await axios
    .get(`${REACT_APP_URL_PATH_API}/users/GetDepartament`)
    .then(function (response) {
      var responsedto = response.data
      return responsedto as ListResponse<DepartmentInterfase[]>
    })
    .catch(function (error) {
      if (error.response) {
        console.log(error.message ?? '')
        return error.response as ListResponse<DepartmentInterfase[]>
      } else {
        console.log(error.message ?? '')
      }
      return [] as unknown as ListResponse<DepartmentInterfase[]>
    })

  return response
}

export const getAllProfile = async (): Promise<
  ListResponse<ProfileInterfase[]> | undefined
> => {
  var response = await axios
    .get(
      `${REACT_APP_URL_PATH_API}/users/GetProfile?ApplicationID=2&StatusID=1`
    )
    .then(function (response) {
      var responsedto = response.data
      return responsedto as ListResponse<ProfileInterfase[]>
    })
    .catch(function (error) {
      if (error.response) {
        console.log(error.message ?? '')
        return error.response as ListResponse<ProfileInterfase[]>
      } else {
        console.log(error.message ?? '')
      }
      return [] as unknown as ListResponse<ProfileInterfase[]>
    })

  return response
}

export const getFilteredUsers = async (
  criteria: string
): Promise<Nullable<UserAccountsInterfase[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/users/DynamicUserAccount?query=${criteria}&MaxQuery=50`
  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data.model
    }
  } catch (error) {}

  return null
}

/**
 * Perfil REAL del usuario dentro de MiTealca (ApplicationID 2).
 *
 * Hace falta porque `DynamicUserAccount` —el buscador de la libreta— devuelve el
 * perfil que la persona tenga en CUALQUIER aplicación, no el de MiTealca. Para
 * enderson.namias eso es "Ti" (61), para tatiana.paez "Especialista De Bases De
 * Datos" (125) y para marilu.perez "Administrador" (198), cuando los tres son
 * Super Administrador (201) acá. Sin esta consulta no hay forma de saber que hay
 * que bloquearles el perfil.
 *
 * `aplicationCode` va con la errata del API (le falta la "p"); es el nombre real
 * del parámetro.
 */
export const getUserProfileInApp = async (
  userID?: string
): Promise<{ profileID?: number; profileName?: string }> => {
  if (!userID) return {}

  try {
    const response = await axios.get(
      `${REACT_APP_URL_PATH_API}/Users/${userID}?aplicationCode=2`
    )
    return {
      profileID: response.data?.profileID ?? undefined,
      profileName: response.data?.profileName ?? undefined,
    }
  } catch (error) {
    // 404 = el usuario no tiene perfil en MiTealca. No es un fallo: se sigue
    // con el perfil que haya traído el listado.
    console.log('No se pudo leer el perfil de MiTealca:', (error as Error)?.message)
    return {}
  }
}

export const getExitsUser = async (
  userID?: string,
  userName?: string,
  email?: string
): Promise<SimpleResponse<UserAccountsExitsInterfase> | undefined> => {
  var response = await axios
    .get(
      `${REACT_APP_URL_PATH_API}/users/ExitsUser?UserID=${
        userID ?? ''
      }&UserName=${userName ?? ''}&Email=${email ?? ''}`
    )
    .then(function (response) {
      var responsedto = response.data
      return responsedto as SimpleResponse<UserAccountsExitsInterfase>
    })
    .catch(function (error) {
      if (error.response) {
        console.log(error.message ?? '')
        return error.response as SimpleResponse<UserAccountsExitsInterfase>
      } else {
        console.log(error.message ?? '')
      }
      return {} as unknown as SimpleResponse<UserAccountsExitsInterfase>
    })

  return response
}

export const createUserApi = async (
  userAccount: UserAccountsInterfase[]
): Promise<UpdStarStandUserInterfase> => {
  let userAccountId = ''
  let userId = ''
  let dirError = false
  let errorMessage = ''
  let fieldErrors: Record<string, string> = {}

  let datamap = userAccount.map((dto: UserAccountsInterfase) =>
    UserAccountMapperDto(dto)
  )

  try {
    const response = await axios.post(
      `${REACT_APP_URL_PATH_API}/users/UpsertUserStandar?nameApplication=MiTealca&withOnBoardingMail=${
        datamap[0].userID === undefined ? 'true' : 'false'
      }`,
      datamap
    )

    const data = response.data as SimpleResponse<UpdStarStandUserInterfase>[]
    if (Array.isArray(data) && !data[0]?.didError) {
      userAccountId = data[0]?.model?.UserAccountID ?? ''
      userId = data[0]?.model?.userID ?? ''
    } else {
      dirError = true
      const mapped = mapUserApiError(data, 'No se pudo guardar el usuario')
      errorMessage = mapped.message
      fieldErrors = mapped.fieldErrors
    }
  } catch (error) {
    dirError = true
    const axiosError = error as AxiosError
    // OJO: cuando el API rechaza por DataAnnotations responde 400 con un
    // ProblemDetails, NO con la lista de SimpleResponse. Leerlo como `data[0]`
    // lanzaba un TypeError y el modal terminaba mostrando "Error:" pelado,
    // justo el caso del largo del nombre.
    const mapped = mapUserApiError(
      axiosError.response?.data,
      axiosError.message ?? 'No se pudo guardar el usuario'
    )
    errorMessage = mapped.message
    fieldErrors = mapped.fieldErrors
    console.log('Error guardando el usuario:', errorMessage)
  }

  return {
    ...datamap[0],
    UserAccountID: userAccountId,
    userID: userId,
    didError: dirError,
    errorMessage: errorMessage,
    fieldErrors: fieldErrors,
  }
}

export const getAllUserClients = async (): Promise<
  ListResponse<UserAccountsCountInterfase[]> | undefined
> => {
  var response = await axios
    .get(`${REACT_APP_URL_PATH_API}/users/GetUserAccountCount?ApplicationID=2`)
    .then(function (response) {
      var responsedto = response.data
      return responsedto as ListResponse<UserAccountsCountInterfase[]>
    })
    .catch(function (error) {
      //dirError= true;
      if (error.response) {
        console.log(error.message ?? '')
        return error.response as ListResponse<UserAccountsCountInterfase[]>
      } else {
        console.log(error.message ?? '')
      }
      return [] as unknown as ListResponse<UserAccountsCountInterfase[]>
    })

  return response
}

export const DeleteUser = async (
  userId?: string,
  userAccountId?: string
): Promise<SingleResponse> => {
  let dirError = false
  let errorMessage = ''

  try {
    await axios
      .post(
        `${REACT_APP_URL_PATH_API}/users/DeleteUserAccount?UserID=${
          userId ?? ''
        }&UserAccountID=${userAccountId ?? ''}`
      )
      .then(function (response) {
        var data = response.data as SingleResponse
        if (data.didError) {
          dirError = true
          errorMessage = data.errorMessage
        }
      })
      .catch(function (error) {
        dirError = true
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          let errorData = error.response.data as SingleResponse
          errorMessage = errorData.errorMessage ?? ''
          console.log('Error generando guia:', errorData.errorMessage)
        } else {
          // Something happened in setting up the request that triggered an Error
          errorMessage = error.message ?? ''
          console.log('Error generando guia:', error.message)
        }
      })
  } catch (error) {
    console.log('Error generando guia:', error)
  }

  return {
    didError: dirError,
    errorMessage: errorMessage,
  } as SingleResponse
}

export const ChangeUserPassword = async (
  userId?: string,
  UserLogin?: string,
  UserPassword?: string,
  Reset?: boolean
): Promise<SingleResponse> => {
  let dirError = false
  let errorMessage = ''

  try {
    await axios
      .post(
        `${REACT_APP_URL_PATH_API}/users/ChangeUserPassword?UserID=${
          userId ?? ''
        }&UserLogin=${UserLogin ?? ''}&UserLogin=${
          UserLogin ?? ''
        }&UserPassword=${UserPassword ?? ''}&Reset=${Reset ?? 'false'}`
      )
      .then(function (response) {
        var data = response.data as SingleResponse
        if (data.didError) {
          dirError = true
          errorMessage = data.errorMessage
        }
      })
      .catch(function (error) {
        dirError = true
        if (error.response) {
          let errorData = error.response.data as SingleResponse
          errorMessage = errorData.errorMessage ?? ''
          console.log('Error generando guia:', errorData.errorMessage)
        } else {
          errorMessage = error.message ?? ''
          console.log('Error generando guia:', error.message)
        }
      })
  } catch (error) {
    console.log('Error generando guia:', error)
  }

  return {
    didError: dirError,
    errorMessage: errorMessage,
  } as SingleResponse
}
